import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { EyeOff, UserX, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Sticky amber banner shown on EVERY page while an Owner is impersonating a
 * practice super_admin. One-click "Return to Owner" swaps the session back.
 *
 * Only renders when `user.impersonating === true` (set by
 * `enrich_public_user` on the backend after the Owner starts impersonation).
 */
export default function ImpersonationBanner() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [returning, setReturning] = useState(false);

  if (!user?.impersonating) return null;

  const stop = async () => {
    if (returning) return;
    setReturning(true);
    try {
      await api.post("/platform/impersonate/stop");
      toast.success("Returned to Owner");
      await refresh();
      navigate("/platform/practices");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not restore Owner session");
    }
    setReturning(false);
  };

  return (
    <div
      className="w-full flex items-center justify-between px-4 sm:px-6 py-2 text-xs font-semibold"
      style={{ background: "#fef3c7", color: "#8b6f2a",
               borderBottom: "1.5px solid #d4b673" }}
      data-testid="impersonation-banner"
    >
      <div className="flex items-center gap-2 min-w-0">
        <EyeOff size={13} className="flex-shrink-0" />
        <span className="hidden sm:inline uppercase tracking-widest">Impersonating</span>
        <span className="truncate">
          You are viewing as <b data-testid="impersonation-target">{user.name || user.email}</b>
          {user.practice_name ? ` · ${user.practice_name}` : ""}
          {user.impersonator_email ? (
            <span className="hidden md:inline"> · Owner: <b>{user.impersonator_email}</b></span>
          ) : null}
        </span>
      </div>
      <button
        onClick={stop}
        disabled={returning}
        className="ml-3 flex-shrink-0 flex items-center gap-1 px-3 py-1 rounded font-semibold text-white uppercase tracking-widest text-[10px] transition-colors"
        style={{ background: "#8b6f2a" }}
        data-testid="impersonation-stop-btn"
      >
        {returning ? <Loader2 size={11} className="animate-spin" /> : <UserX size={11} />}
        {returning ? "Restoring…" : "Return to Owner"}
      </button>
    </div>
  );
}
